/**
 * The bookshelf: every route of SATU in the five sentence-case groups of CONTENT.md
 * section 1, filtered by role before render. The sidebar reads `navForRole`, the phone bar
 * `bottomBarForRole`, the search palette `navItems`.
 */
import { PERAN, type Peran, uiStrings } from '@satu/shared'
import {
  ArrowLeftRight,
  ChartColumn,
  CircleUserRound,
  Contact,
  Droplet,
  Feather,
  FileText,
  Flame,
  HandHeart,
  Heart,
  House,
  Import,
  Landmark,
  type LucideIcon,
  MapPinned,
  RefreshCw,
  Settings,
  ShieldCheck,
  UserPlus,
  Users,
  Wheat,
} from 'lucide-react'

export type Role = Peran
export const ROLES: readonly Role[] = PERAN

/** The roles' words; "Ketua {unit}" takes the parish's word (`labelPeran`). */
export const ROLE_LABEL: Readonly<Record<Role, string>> = uiStrings.peran

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  roles: readonly Role[]
  /** Match the path exactly (Beranda at `/`). */
  end?: boolean
}

export interface NavGroup {
  /** null for the ungrouped Beranda row. */
  label: string | null
  items: readonly NavItem[]
}

const SEMUA: readonly Role[] = PERAN
const PAROKI: readonly Role[] = ['admin_paroki', 'staf_paroki']
/**
 * The parish roles and both ketua: a ketua stasi has exactly the ketua lingkungan's routes
 * (spec 2026-09-30, 7).
 */
const PAROKI_DAN_LINGKUNGAN: readonly Role[] = [...PAROKI, 'ketua_lingkungan', 'ketua_stasi']
export const KEUSKUPAN: readonly Role[] = ['admin_keuskupan', 'uskup', 'dekan']
const PAROKI_DAN_KEUSKUPAN: readonly Role[] = [...PAROKI, ...KEUSKUPAN]
/**
 * Release 32 (owner, 2026-11-02): what a Pastor Paroki reads (Keluarga and Umat, the books, Surat,
 * Verifikasi, Peta, Laporan) on top of the routes of every role; never Pendaftaran, Mutasi, Impor,
 * Pelayan & pengurus, Administrasi or Pengaturan.
 */
const PASTOR: Role = 'pastor_paroki'
const BACA_PAROKI: readonly Role[] = [...PAROKI, PASTOR]

const ADMINISTRASI: NavItem = {
  to: '/admin',
  label: 'Administrasi',
  icon: Landmark,
  roles: ['admin_keuskupan'],
}

export const NAV_GROUPS: readonly NavGroup[] = [
  { label: null, items: [{ to: '/', label: 'Beranda', icon: House, roles: SEMUA, end: true }] },
  {
    label: 'Umat',
    items: [
      {
        to: '/keluarga',
        label: 'Keluarga',
        icon: Users,
        roles: [...PAROKI_DAN_LINGKUNGAN, PASTOR],
      },
      { to: '/umat', label: 'Umat', icon: Contact, roles: [...PAROKI_DAN_LINGKUNGAN, PASTOR] },
      { to: '/pendaftaran', label: 'Pendaftaran', icon: UserPlus, roles: PAROKI_DAN_LINGKUNGAN },
      // Peta umat (release 20): the parish's families, a ketua's lingkungan.
      {
        to: '/peta',
        label: uiStrings.peta.navigasi,
        icon: MapPinned,
        roles: [...PAROKI_DAN_LINGKUNGAN, PASTOR],
      },
    ],
  },
  {
    label: 'Buku Paroki',
    items: [
      { to: '/buku/baptis', label: 'Baptis', icon: Droplet, roles: BACA_PAROKI },
      { to: '/buku/komuni', label: 'Komuni Pertama', icon: Wheat, roles: BACA_PAROKI },
      { to: '/buku/krisma', label: 'Krisma', icon: Flame, roles: BACA_PAROKI },
      { to: '/buku/perkawinan', label: 'Perkawinan', icon: Heart, roles: BACA_PAROKI },
      { to: '/buku/kematian', label: 'Kematian', icon: Feather, roles: BACA_PAROKI },
    ],
  },
  {
    label: 'Surat',
    items: [
      { to: '/surat', label: 'Surat', icon: FileText, roles: BACA_PAROKI },
      { to: '/mutasi', label: 'Mutasi', icon: ArrowLeftRight, roles: PAROKI },
      {
        to: '/verifikasi',
        label: 'Verifikasi',
        icon: ShieldCheck,
        roles: [...PAROKI_DAN_KEUSKUPAN, PASTOR],
      },
    ],
  },
  {
    label: 'Laporan',
    items: [
      { to: '/laporan', label: 'Laporan', icon: ChartColumn, roles: SEMUA },
      // Peta umat (release 20) for the admin keuskupan's diocese view: what the device holds, a
      // parish picker on the screen. After Laporan, so its phone bar stays as it was. The uskup
      // and a Ketua Wilayah have no Peta (ruling 2, @satu/shared `PERAN_PETA`).
      { to: '/peta', label: uiStrings.peta.navigasi, icon: MapPinned, roles: ['admin_keuskupan'] },
      // An admin keuskupan reaches it inside a parish (`navUntukSesi`), never in the diocese view.
      { to: '/impor', label: 'Impor', icon: Import, roles: PAROKI },
    ],
  },
  {
    label: 'Perangkat',
    items: [
      { to: '/sinkron', label: 'Sinkronisasi', icon: RefreshCw, roles: SEMUA },
      { to: '/pelayan', label: 'Pelayan & pengurus', icon: HandHeart, roles: ['admin_paroki'] },
    ],
  },
  {
    label: 'Keuskupan',
    items: [
      ADMINISTRASI,
      { to: '/pelayan', label: 'Pelayan & pengurus', icon: HandHeart, roles: ['admin_keuskupan'] },
    ],
  },
]

/**
 * Plan 9b (spec 2026-10-02 upacara-bersama-dan-akun 5.3): the person's own page and the parish
 * settings, in no group of the shelf. The side panel holds them at its bottom, above "Perkecil
 * navigasi" (Akun with the person's name and role); the phone's Lainnya ends with them. Only
 * Admin Paroki sees Pengaturan; any other role that opens it is taken to Akun.
 */
export const NAV_AKUN: readonly NavItem[] = [
  { to: '/akun', label: uiStrings.akun.judul, icon: CircleUserRound, roles: SEMUA },
  { to: '/pengaturan', label: uiStrings.pengaturan.judul, icon: Settings, roles: ['admin_paroki'] },
]

export function navAkunForRole(role: Role): NavItem[] {
  return NAV_AKUN.filter((item) => item.roles.includes(role))
}

/** The groups a role sees, in shelf order, with empty groups dropped. */
export function navForRole(role: Role): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.filter((item) => item.roles.includes(role)),
  })).filter((group) => group.items.length > 0)
}

/**
 * Plan 10 (spec 2026-10-02 admin-keuskupan-di-paroki, 4): the shelf of the session as it works.
 * An admin keuskupan working in a parish (`diParoki`; its session is that parish's Admin Paroki)
 * has that parish's Admin Paroki shelf, Pelayan & pengurus included, and keeps Administrasi under
 * Keuskupan. The reports follow the parish; the diocese's figures are the Keuskupan view's.
 */
export function navUntukSesi(peran: Role, diParoki: boolean): NavGroup[] {
  if (!diParoki) return navForRole(peran)
  return [...navForRole('admin_paroki'), { label: 'Keuskupan', items: [ADMINISTRASI] }]
}

export function navItems(groups: readonly NavGroup[]): NavItem[] {
  return groups.flatMap((group) => [...group.items])
}

export interface BottomBar {
  /** At most four routes; Lainnya, the sheet's trigger, is always the last slot. */
  items: NavItem[]
  /** Groups listed in the Lainnya sheet; the last holds Akun (and Pengaturan), plan 9b. */
  lainnya: NavGroup[]
}

// CONTENT.md section 1: staff bars end with Lainnya. The ketua lingkungan bar ends with
// Lainnya too, deviating from CONTENT.md (which ends it with Pengaturan) by decision of
// 2026-09-30: once the ketua has more than five routes (Umat, Laporan), a bar of five fixed
// routes leaves the rest unreachable on a phone. A ketua stasi has the same bar. Diocese
// roles are not specified there and follow the same shape: the first three routes (or all, up
// to four), Sinkronisasi, then Lainnya, which since plan 9b always ends with Akun.
const BAR_PAROKI_DAN_LINGKUNGAN = ['/', '/keluarga', '/pendaftaran', '/sinkron'] as const
/** Release 32: a Pastor Paroki's bar puts Surat where the staff have Pendaftaran. */
const BAR_PASTOR = ['/', '/keluarga', '/surat', '/sinkron'] as const

export function bottomBarForRole(role: Role): BottomBar {
  return bottomBarDari(role, navForRole(role))
}

/** Plan 10: the phone bar of the session as it works (`navUntukSesi`). */
export function bottomBarUntukSesi(peran: Role, diParoki: boolean): BottomBar {
  return bottomBarDari(diParoki ? 'admin_paroki' : peran, navUntukSesi(peran, diParoki))
}

function bottomBarDari(role: Role, groups: NavGroup[]): BottomBar {
  const items = navItems(groups)
  const byPath = (paths: readonly string[]): NavItem[] =>
    paths.flatMap((path) => items.filter((item) => item.to === path))

  let bar: NavItem[]
  if (PAROKI_DAN_LINGKUNGAN.includes(role)) bar = byPath(BAR_PAROKI_DAN_LINGKUNGAN)
  else if (role === PASTOR) bar = byPath(BAR_PASTOR)
  else if (items.length <= 4) bar = items
  else {
    const utama = items.filter((item) => !['/sinkron', '/admin'].includes(item.to))
    bar = [...utama.slice(0, 3), ...byPath(['/sinkron'])]
  }

  // Plan 9b (5.3): Lainnya always ends with Akun (and Pengaturan for admin paroki), so a bar
  // holds at most four routes and the fifth slot opens Lainnya.
  const sisa = groups
    .map((group) => ({ label: group.label, items: group.items.filter((i) => !bar.includes(i)) }))
    .filter((group) => group.items.length > 0)
  return { items: bar, lainnya: [...sisa, { label: null, items: navAkunForRole(role) }] }
}
